<script setup lang="ts">
import Avatar from '~/components/Avatar.vue'
import Button from '~/components/Common/Button/Button.vue'
import type { FetchError } from 'ofetch'
import type { Profile } from '~/types/profile'
import type { ToastNotification } from '~/components/Toast.vue'

const refInputEl = ref<HTMLElement>()
const isAvatarUploading = ref(false)

const isAuthed = inject('isAuthed') as Ref<boolean>
const profile = inject('profile') as Ref<Profile>
const toast = inject('toast') as Ref<ToastNotification>

async function uploadProfileAvatar(file: File, profile: Profile): Promise<Profile> {

	const fileReader = new FileReader()

	fileReader.readAsDataURL(file)

	return new Promise<Profile>((resolve, reject) => {
		fileReader.onload = async () => {
			if (typeof fileReader.result === 'string') {
				try {
					const result = await fetch('https://n8n.healthnutrition.com/webhook/profile/avatar', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							user_id: profile.id,
							avatar_url: fileReader.result,
						}),
					})

					const response = await result.json()

					const { $llana } = useNuxtApp()

					const userUpdate = (await $llana<Profile>({
						type: 'UPDATE',
						table: 'users',
						id: profile.id,
						data: {
							avatar_url: response.avatar_url,
						},
					})) as Profile

					if (!userUpdate?.id) {
						reject(new Error('Error updating profile. Please try again.'))
					} else {
						resolve(userUpdate)
					}
				} catch (error) {
					reject(error)
				}
			} else {
				reject(new Error('Cannot read filetype.'))
			}
		}
	})
}

async function uploadAvatar(file: Event) {

	try {
		isAvatarUploading.value = true

		const { files } = file.target as HTMLInputElement

		if (!files?.length) {
			toast.value = {
				show: true,
				timeout: 10,
				title: `No file selected`,
				message: `Please select a file to upload.`,
				type: 'error',
			}
			isAvatarUploading.value = false
			return
		}

		profile.value = await uploadProfileAvatar(files[0], profile.value)

		toast.value = {
			show: true,
			timeout: 10,
			title: `Avatar uploaded`,
			message: `Your avatar has been uploaded successfully.`,
			type: 'success',
		}
	} catch (err: any) {
		err = err as FetchError
		console.error(err)
		toast.value = {
			show: true,
			timeout: 10,
			title: `Error uploading avatar`,
			message: `There was an error uploading your avatar, please try again later.`,
			type: 'error',
		}
	} finally {
		isAvatarUploading.value = false
	}
}
</script>
<template>
	<div class="grid grid-cols-1 gap-x-2 gap-y-8 sm:max-w-xl sm:grid-cols-6">
		<div class="col-span-full flex items-center gap-x-8">
			<Avatar :isAuthed="isAuthed" :self="profile" :profile="profile" :size="20" type="rounded" />
			<div>
				<Button
					:disabled="isAvatarUploading"
					@click="refInputEl?.click()"
					type="button"
					>
					<Icon name="ph:upload-duotone" class="mr-2" />
					{{
						isAvatarUploading
							? 'Uploading Avatar...'
							: profile.avatar_url
							? 'Change avatar'
							: 'Upload avatar'
					}}
				</Button>
				<input
					ref="refInputEl"
					type="file"
					name="file"
					accept=".jpeg,.png,.jpg,GIF"
					hidden
					@input="uploadAvatar" />
				<p class="mt-2 text-xs/5 text-gray-400">JPG, GIF or PNG. 1MB max.</p>
			</div>
		</div>
	</div>
</template>
